import {
  useCallback,
  useEffect,
  useState,
} from 'react';
import {
  PCCProgram,
} from 'dtos/pCCProgram';
import {
  getEligiblePCCPrograms,
  PCCProgramStatus,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/PCC/utils';

type PCCPopoverScreen = {
  selectedProgramTypes: PCCProgram[];
  hasEligiblePrograms: boolean;
  handleToggleProgram: (program: PCCProgramStatus, checked: boolean) => void;
  handleEnrollSelected: () => Promise<void>;
};

export default function usePCCPopoverScreen(
  programs: PCCProgramStatus[],
  onEnrollSelected: (programs: PCCProgram[]) => Promise<void>,
): PCCPopoverScreen {
  const eligiblePrograms = getEligiblePCCPrograms(programs);
  const [selectedProgramTypes, setSelectedProgramTypes] = useState<PCCProgram[]>(
    () => eligiblePrograms.map((program) => program.type),
  );

  useEffect(() => {
    const eligibleTypeSet = new Set(
      getEligiblePCCPrograms(programs).map((program) => program.type),
    );
    setSelectedProgramTypes((current) => {
      const next = current.filter((programType) => eligibleTypeSet.has(programType));
      return next.length === current.length ? current : next;
    });
  }, [programs]);

  const handleToggleProgram = useCallback((
    program: PCCProgramStatus,
    checked: boolean,
  ) => {
    setSelectedProgramTypes((current) => {
      if (checked) {
        return current.includes(program.type) ? current : [...current, program.type];
      }

      return current.filter((programType) => programType !== program.type);
    });
  }, []);

  const handleEnrollSelected = useCallback(async () => {
    await onEnrollSelected(selectedProgramTypes);
  }, [onEnrollSelected, selectedProgramTypes]);

  return {
    selectedProgramTypes,
    hasEligiblePrograms: eligiblePrograms.length > 0,
    handleToggleProgram,
    handleEnrollSelected,
  };
}
